<script setup lang="ts">
import { $brandingGlobalLineItemOptions, $getBrandingChart } from '@/api/brandings'
import router from '@/router'
import { uniq, flattenDeep } from 'lodash'
import type { RouteLocationPathRaw } from 'vue-router'

// components
const campaignsSelectFilter = ref()

const selectedChartType = ref('count')
const chartTypes = [
  {
    value: 'count', // items count
    title: 'Count',
    desc: 'Items count',
  },
  {
    value: 'qty', // items count * qty
    title: 'Qty',
    desc: 'Total items qty',
  }
]
const selectedProjects = ref([])
const projectOptions = ref([])
const selectedPrograms = ref([])
const programOptions = ref([])
const selectedSuppliers = ref([])
const supplierOptions = ref([])

const optionsLoading = ref<boolean>(false)

const brandingItemsChartData = ref<any[]>([])
const series = ref<any[]>([])
const total = ref<number>(0)

const chartOptions = ref({
  stroke: { width: 0 },
  labels: [] as string[],
  colors: [] as string[],
  dataLabels: {
    enabled: true,
    formatter: (val: string) => `${Number.parseInt(val, 10)}%`,
  },
  grid: {
    padding: {
      top: 0,
      right: 0,
      bottom: 72,
      left: 0
    }
  },
  legend: {
    floating: true,
    showForSingleSeries: true,
    position: 'bottom',
    markers: { offsetX: -1 },
    fontSize: '13px',
    labels: { colors: '#000' },
    itemMargin: {
      vertical: 2,
      horizontal: 7,
    },
  },
  plotOptions: {
    pie: {
      dataLabels: {
        minAngleToShowLabel: 0,
        offset: 0,
      },
      donut: {
        labels: {
          show: true,
          name: {
            fontSize: '1.125rem',
            color: '#000',
          },
          value: {
            fontSize: '1.125rem',
            color: '#000',
            formatter: (val: string) => `${Number.parseInt(val, 10)}`,
          },
          total: {
            show: true,
            fontSize: '1.125rem',
            label: 'Total Items',
            formatter: () => total.value,
            color: '#000',
          },
        },
      },
    },
  },
  responsive: [
    {
      breakpoint: 992,
      options: {
        chart: {
          height: 380,
        },
        legend: {
          position: 'bottom',
        },
      },
    },
    {
      breakpoint: 576,
      options: {
        chart: {
          height: 320,
        },
        plotOptions: {
          pie: {
            dataLabels: {
              minAngleToShowLabel: 0,
            },
            donut: {
              labels: {
                show: true,
                name: {
                  fontSize: '0.9375rem',
                },
                value: {
                  fontSize: '0.9375rem',
                },
                total: {
                  fontSize: '0.9375rem',
                },
              },
            },
          },
        },
      },
    },
  ],
})

const getChart = async () => {
  total.value = 0
  series.value = []
  chartOptions.value.labels = []
  chartOptions.value.colors = []

  const filterProjects = selectedProjects.value.filter((i: any) => !i?.selectedWorkOrders?.length)
    .map((i: any) => i.id)
  const filterProjectWorkOrders = selectedProjects.value.filter((i: any) => i?.selectedWorkOrders?.length)
    .map((i: any) => i.selectedWorkOrders)
  const filterWorkOrders = uniq(flattenDeep(filterProjectWorkOrders))
  try {
    const res = await $getBrandingChart({
      chart_type: selectedChartType.value,
      projects: filterProjects,
      work_orders: filterWorkOrders,
      event_groups_or_children: selectedPrograms.value.length ?
        selectedPrograms.value.map((i: any) => i.id) : undefined,
      work_order__campaign__suppliers__in: selectedSuppliers.value.length ?
        selectedSuppliers.value.map((i: any) => i.id) : undefined,
    })
    res.data.map((item: any) => {
      if (item?.item_status__name) {
        series.value.push(item.status_no)
        total.value += item.status_no
        chartOptions.value.labels.push(item.item_status__name)
        chartOptions.value.colors.push(item.item_status__colour)
      }
    })
    brandingItemsChartData.value = res.data
  } catch (e) {
    console.log(e)
  }
}

const getGlobalSearchLineItemsPageLink = (
  itemStatus: {item_status_id: number, item_status__name: string} | null = null
) => {
  const query = {
    programs: selectedPrograms.value?.length ? JSON.stringify(selectedPrograms.value) : [],
    projects: selectedProjects.value?.length ? JSON.stringify(selectedProjects.value.map((project: any) => {
      // exclude redundant key 'work_orders' for query
      if (project.hasOwnProperty('work_orders')) {
        const { work_orders, ...rest } = project
        return rest
      }
      return project
    })) : [],
    suppliers: selectedSuppliers.value?.length ? JSON.stringify(selectedSuppliers.value) : []
  } as {
    programs?: string | any[]
    projects?: string | any[]
    suppliers?: string | any[]
    item_statuses?: string
  }

  if (!!itemStatus?.item_status_id) {
    query.item_statuses = JSON.stringify([{id: itemStatus?.item_status_id, name: itemStatus?.item_status__name}])
  }

  // Filter out empty or null or undefined values or empty arrays from the query
  const filteredQuery = Object.fromEntries(
    Object.entries(query).filter(([key, value]) => {
      if (Array.isArray(value)) {
        // Filter out empty arrays
        return value.length > 0
      } else {
        // Filter out empty, null, and undefined values
        return value !== '' && value !== null && value !== undefined
      }
    })
  )

  // If filteredQuery is empty, fill it with { clear_filters: true }
  const finalQuery = Object.keys(filteredQuery).length === 0 ? { clear_filters: true } : filteredQuery

  const path = '/projects/branding/global-search-line-items'

  return {
    path: path,
    query: finalQuery
  } as RouteLocationPathRaw
}

const getGlobalSearchLineItemsPageResolvedLink = () => {
  const route = router.resolve(getGlobalSearchLineItemsPageLink())
  return route.href
}

const onPieChartDataPointSelection = async (e: any, chartContext: any, config: any) => {
  const itemStatus = brandingItemsChartData.value[config.dataPointIndex]
  openGlobalSearchLineItemsPage(itemStatus)
}

const openGlobalSearchLineItemsPage = (itemStatus = null) => {
  const globalSearchLineItemsPageLink = getGlobalSearchLineItemsPageLink(itemStatus)

  // New Window link
  const resolvedPath = router.resolve(globalSearchLineItemsPageLink)
  window.open(resolvedPath.href, '_blank')
}

const getSelectOptions = async () => {
  optionsLoading.value = true
  const payload = {
    filters: JSON.stringify({
      work_order__campaign__in: selectedProjects?.value?.length ? selectedProjects.value.map((i: any) => i.id) : undefined,
      work_order__campaign__event_group_or_children__in: selectedPrograms?.value?.length ?
        selectedPrograms.value.map((i: any) => i.id) : undefined,
      work_order__campaign__suppliers__in: selectedSuppliers.value.length ?
        selectedSuppliers.value.map((i: any) => i.id) : undefined,
    }),
    is_main_page_options_only: true
  }
  try {
    const res = await $brandingGlobalLineItemOptions(payload)
    projectOptions.value = res.data.campaign_options
    programOptions.value = res.data.event_group_options
    supplierOptions.value = res.data?.supplier_options || []
  } catch (e) {
    console.log(e)
  } finally {
    optionsLoading.value = false
  }
}

onMounted(() => getChart())

watch(selectedChartType, () => {
  // Update Total Label
  chartOptions.value.plotOptions.pie.donut.labels.total.label =
    selectedChartType.value === 'count' ? 'Total Items' : 'Total Items Qty'

  // Fetch updated chart
  getChart()
})
</script>

<template>
  <VCard variant="outlined" class="card-with-shadow">
    <VCardTitle>
      <VRow class="pa-4">
        <VCol
          cols="12"
          class="d-flex flex-wrap"
        >
          <div class="d-flex align-center mr-1">
            Branding Items
          </div>
<!--          <VBtnToggle-->
<!--            v-model="selectedChartType"-->
<!--            color="primary"-->
<!--            class="branding-chart-type-btn-toggle"-->
<!--            variant="outlined"-->
<!--            mandatory-->
<!--            divided-->
<!--          >-->
<!--            <template-->
<!--              v-for="type in chartTypes"-->
<!--              :key="type.value"-->
<!--            >-->
<!--              <VBtn-->
<!--                :value="type.value"-->
<!--                class="p-1"-->
<!--              >-->
<!--                <div class="d-flex align-center flex-column justify-center">-->
<!--                  <p class="text-title mb-0">-->
<!--                    {{ type.title }}-->
<!--                  </p>-->
<!--                  <p class="text-desc mb-0">-->
<!--                    {{ type.desc }}-->
<!--                  </p>-->
<!--                </div>-->
<!--              </VBtn>-->
<!--            </template>-->
<!--          </VBtnToggle>-->
        </VCol>
        <VCol cols="12" md="6">
          <Suspense>
            <MultipleEventGroupSelect
              v-model="selectedPrograms"
              :select-options="programOptions"
              :loading="optionsLoading"
              active-branding-campaigns-only
              class="mr-2"
              @update:focused="programOptions.length ? '' : getSelectOptions()"
              @update:model-value="getChart(); getSelectOptions()"
            />
          </Suspense>
        </VCol>
        <VCol cols="12" md="6">
          <Suspense>
            <MultipleCampaignsSelect
              v-model="selectedProjects"
              :select-options="projectOptions"
              :type="2"
              :loading="optionsLoading"
              @update:focused="projectOptions.length ? '' : getSelectOptions()"
              @update:modelValue="getChart(); getSelectOptions()"
            />
          </Suspense>
        </VCol>
        <VCol cols="12">
          <Suspense>
            <MultipleSuppliersSelect
              v-model="selectedSuppliers"
              :select-options="supplierOptions"
              :loading="optionsLoading"
              @update:focused="supplierOptions.length ? '' : getSelectOptions()"
              @update:modelValue="getChart(); getSelectOptions()"
            />
          </Suspense>
        </VCol>
      </VRow>
    </VCardTitle>
    <VCardText>
      <VRow>
        <VCol
          cols="12"
          class="mt-auto"
        >
          <apexchart
            v-if="series.length"
            type="donut"
            :options="chartOptions"
            :series="series"
            height="400"
            @dataPointSelection="onPieChartDataPointSelection"
          />
          <template v-else>
            <div class="pa-4 d-flex align-center justify-center">
              <h3>No Items in selected project</h3>
            </div>
          </template>
        </VCol>
      </VRow>
    </VCardText>
    <VCardText>
      <VBtn
        class="text-decoration-none"
        :href="getGlobalSearchLineItemsPageResolvedLink()"
        target="_blank"
      >
        View All Items
      </VBtn>
    </VCardText>
  </VCard>
</template>

<style lang="scss" scoped>
.card-list {
  --v-card-list-gap: 18px;
}

.branding-chart-type-btn-toggle {
  height: auto !important;
  .text-title {
    font-size: 14px;
    line-height: 16px;
  }
  .text-desc {
    font-size: 10px;
    line-height: 12px;
  }
}
</style>
